/**
 * Resolving a recurring campaign's audience — the dynamic half of the
 * locked-vs-dynamic rule settled 2026-08-21 (see
 * `claude/scheduled-sends-rebuild-scope.md`): a one-off blast's audience is a
 * frozen list of names, resolved once and never re-worked (`AudienceRule` in
 * `campaigns.ts` — untouched by this file, on purpose). A recurring
 * campaign's audience is a FILTER, re-resolved fresh each time an occurrence
 * is generated.
 *
 * This deliberately does NOT reimplement filter matching. `selectAgents` in
 * `filters.ts` is the one place "which agents" is answered anywhere in this
 * app — on screen, in tab counts, in exports — specifically so that question
 * is never answered twice and cannot quietly disagree with itself. A
 * scheduled campaign's audience is the exact same question asked by a
 * schedule instead of by Renee looking at the roster, so it gets the exact
 * same answer.
 *
 * What IS new here: `selectedOnly` (a hand-ticked mailing list) has no place
 * in a recurring audience — a ticked list is a snapshot by construction and
 * can never notice a new joiner, which is the one thing a recurring campaign
 * must do. `ScheduledAudienceRule` leaves it out entirely rather than
 * allowing it and hoping nobody sets it.
 */
import type { Agent } from './types'
import { FILTERS, TAB_LABEL, type FilterContext, type FilterState, type TabKey, selectAgents } from './filters'

export type ScheduledAudienceRule = {
  tab: TabKey
  filters: FilterState
  /**
   * Allowed, for consistency with `selectAgents`, but worth flagging: a saved
   * search string is a strange thing to re-run on a schedule (it is built for
   * finding one or two specific people, not for defining an ongoing group).
   * The create-flow UI is not obligated to expose a search box for this —
   * that is a UI choice, not a schema limitation.
   */
  search: string
  includeDeparted: boolean
}

/**
 * The list of agents this rule matches RIGHT NOW.
 *
 * Called exactly once per occurrence, at the moment that occurrence is
 * generated — see `scheduled-sends-rebuild-scope.md`, "resolve once, never
 * again." The caller freezes the result into that occurrence's own
 * `campaign_sends` rows; this function itself holds nothing and remembers
 * nothing between calls.
 */
export function resolveScheduledAudience(
  rule: ScheduledAudienceRule,
  all: Agent[],
  ctx: FilterContext,
): Agent[] {
  return selectAgents(
    all,
    { tab: rule.tab, filters: rule.filters, search: rule.search, includeDeparted: rule.includeDeparted },
    ctx,
  )
}

/** Just the ids, which is the shape `campaign_sends` rows actually need. */
export function resolveScheduledAudienceIds(
  rule: ScheduledAudienceRule,
  all: Agent[],
  ctx: FilterContext,
): string[] {
  return resolveScheduledAudience(rule, all, ctx).map((a) => a.rezen_id)
}

/**
 * Who a recurring campaign targets, in words — `Scheduled.tsx`'s "who/what
 * group" detail (2026-08-22, per Chris asking to see it on the card rather
 * than only in the create-flow's own confirm screen).
 *
 * Reads the same `TAB_LABEL` and `FILTERS` the rest of the app uses to name
 * these things — the create-flow's confirm screen, the tab counts, the
 * roster toolbar — rather than a second copy of "what does this filter
 * mean" that could quietly disagree with them. Filter VALUES are used as
 * written (an area, an MLS, a tag) because that is what `FilterState`
 * already stores — a display-ready string, not a code needing another
 * lookup — so no options list is needed here to read one back.
 */
export function describeScheduledAudience(rule: Pick<ScheduledAudienceRule, 'tab' | 'filters' | 'search'>): string {
  const parts: string[] = [TAB_LABEL[rule.tab] ?? rule.tab]
  for (const def of FILTERS) {
    const v = rule.filters[def.key]
    if (v) parts.push(`${def.label}: ${v}`)
  }
  if (rule.search) parts.push(`matching "${rule.search}"`)
  return parts.join(' · ')
}
